import { useState, useEffect } from 'preact/hooks';
import { connectSocket, disconnectSocket, isConnected } from '../lib/socket';

type ConnectionStatus = 'disconnected' | 'connected' | 'error';

/**
 * Open (and keep) the app-wide Socket.IO connection. Credentials are resolved
 * by the socket itself from localStorage on every (re)connection attempt
 * (guest-first, with JWT refresh — see `lib/socket.ts`), so this hook only
 * needs an identity KEY: null means "no credentials, stay disconnected", and
 * a key change (e.g. a guest session created mid-session after joining a
 * crew) forces a fresh handshake under the new identity.
 */
export function useSocket(credentialKey: string | null): ConnectionStatus {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');

  useEffect(() => {
    if (!credentialKey) {
      setStatus('disconnected');
      return;
    }

    const socket = connectSocket();

    const handleConnect = () => setStatus('connected');
    const handleDisconnect = () => setStatus('disconnected');
    const handleError = () => setStatus('error');

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleError);

    // If already connected (reconnection scenario)
    if (isConnected()) {
      setStatus('connected');
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleError);
      disconnectSocket();
    };
  }, [credentialKey]);

  return status;
}
